import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import CustomDatePicker from "../CustomDatePicker/CustomDatePicker";
import Datatable from "../Common/Datatable";
import { getColumns } from "../Meetings/meetingsTableConfig";
import { getPresetDateRange, TimelineItem } from "../../utils/utils";
import OpportunitiesService from "../../services/opportunities/OpportunitiesService";
import {
  OpportunityDetailsProps,
  OpportunityInteractionData,
  OpportunityNavigationState,
} from "../../interfaces/opportunities";
import OpportunityDetailsGrid from "./OpportunityDetailsGrid";
import InteractionsTimeline from "../Common/InteractionsTimeline";
import "../../styles/opportunityDetails.css";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import SalesForceIcon from "../../assets/accounts/salesforce-icon.svg";
import useGlobalContext from "../../context/useGlobalContext";

const OpportunityDetails: React.FC<OpportunityDetailsProps> = ({
  opportunity,
  stakeholders,
  parentDateRange,
  navigationState,
}) => {
    const {impersonationDetails} = useGlobalContext();
  const initialRange = getPresetDateRange("last3MonthsFull");
  const { showSnackbar } = useDisplaySnackbar();
  const [localDateRange, setLocalDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>(() => {
    if (navigationState?.detailsDateRange?.startDate && navigationState?.detailsDateRange?.endDate) {
      return navigationState.detailsDateRange;
    }
    if (parentDateRange?.startDate && parentDateRange?.endDate) {
      return {
        startDate: parentDateRange.startDate,
        endDate: parentDateRange.endDate,
      };
    }
    return {
      startDate: initialRange.start_time,
      endDate: initialRange.end_time,
    };
  });

  const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
  const accessLevel = impersonationDetails?.accessType;
  useEffect(() => {
    if (parentDateRange?.startDate && parentDateRange?.endDate && !navigationState?.detailsDateRange) {
      setLocalDateRange({
        startDate: parentDateRange.startDate,
        endDate: parentDateRange.endDate,
      });
      setPageNumber(1);
    }
  }, [parentDateRange?.startDate, parentDateRange?.endDate]);


  const [pageNumber, setPageNumber] = useState<number>(() => navigationState?.detailsPageNumber || 1);
  const [pageSize, setPageSize] = useState<number>(() => navigationState?.detailsPageSize || 50);
  const [sort, setSort] = useState<{ sort_field: string; sort_order?: "asc" | "desc" }>(() =>
    navigationState?.detailsSort || {
      sort_field: "start_time",
      sort_order: "desc",
    }
  );

  const navigationContext = useMemo(() => {
    const parentId = navigationState?.parent_page_identifier || "opportunities";
    const currentNavState: OpportunityNavigationState = {
      ...navigationState,
      isSlideOverOpen: true,
      selectedOpportunity: opportunity,
      detailsDateRange: localDateRange,
      detailsPageNumber: pageNumber,
      detailsPageSize: pageSize,
      detailsSort: sort,
    };

    const baseAccountView = navigationState?.accountViewState;
    const accountView = baseAccountView
      ? {
          ...baseAccountView,
          opportunityNavigationState: currentNavState,
        }
      : undefined;

    return {
      parent_page_identifier: parentId,
      ParentPageIdentifier: parentId,
      opportunityNavigationState: currentNavState,
      accountViewState: accountView,
      activeView: navigationState?.isAccountSlideOver ? "opportunity" : undefined,
      slideOverParent: navigationState?.isAccountSlideOver ? "account-parent-page" : undefined,
      isSlideOverOpen: navigationState?.isAccountSlideOver ? true : undefined,
      clientDomain: accountView?.clientDomain,
      clientName: accountView?.clientName,
      orgId: accountView?.clientOrgId,
      subOrgId: accountView?.subOrgId,
      salesforceAccountId: accountView?.salesforceAccountId,
      salesforceAccountUrl: accountView?.salesforceAccountUrl,
    };
  }, [navigationState, opportunity, localDateRange, pageNumber, pageSize, sort]);

  const {
    data: detailsData,
    isLoading: isDetailsLoading,
    isError: isDetailsError,
    error: DetailError,
  } = OpportunitiesService.useGetOpportunityDetails(
    {
      limit: pageSize,
      offset: pageNumber,
      sortOrder: sort.sort_order ?? "desc",
      sortBy: sort.sort_field,
      mappingId: opportunity?.id || "",
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
    },
    {
      startTime: localDateRange.startDate,
      endTime: localDateRange.endDate,
    },
    !!opportunity?.id
  );

  useEffect(() => {
    if (isDetailsError) {
      showSnackbar("error", DetailError?.message || "Failed to load Opportunity details.");
    }
  }, [isDetailsError, DetailError]);

  const timelineItems = useMemo<TimelineItem[]>(() => {
    if (Array.isArray(detailsData?.interactions) && detailsData?.interactions.length > 0) {
      return detailsData.interactions.map((item) => ({
        id: item.interactionId,
        start_time: item.startTime,
        type: "interaction" as const,
        title: item.title,
        overallSentiment: item.overallSentiment || undefined,
      }));
    }
    return [];
  }, [detailsData?.interactions, detailsData?.interactionData]);

  // Generate rows compatible with getColumns from meetingsTableConfig
  const rows = useMemo(() => {
    const list = Array.isArray(detailsData?.interactionData)
      ? detailsData.interactionData
      : [];

    return list.map((item: OpportunityInteractionData) => {
      return {
        id: item.interactionId,
        title: item.title,
        is_external: item.isExternal,
        start_time: item.startTime,
        end_time: item.endTime,
        duration: item.duration,
        meeting_sentiment: item.rating,
        abstractiveScore: item.rating,
        rating_source: item.ratingSource,
        isPresentAsset: item.isPresentAsset,
        isPreviousAsset: item.isPreviousAsset,
        ...navigationContext,
      };
    });
  }, [detailsData?.interactionData, navigationContext]);

  const columns = useMemo(() => getColumns(), []);

  const tableColumnVisibility = useMemo(
    () => ({
      title: true,
      start_time: true,
      duration: true,
      meeting_sentiment: false,
      rating_source: false,
      momDocLink: false,
      sharedDate: false,
      account: false,
      opportunityName: false,
      actual_duration: false,
      is_manager: false,
      abstractiveScore: false,
      talkTrack: false,
      shareIcon: false,
    }),
    []
  );

  return (
    <Box className="opportunity-details-wrapper">
      <Box className="opportunity-details-header">
        <Box className="opportunity-details-title-row">
          <Typography className="opportunity-details-title">
            {opportunity?.opportunityName || "Opportunity Details"}
          </Typography>

          {opportunity?.salesforceOpportunityUrl && (
            <Tooltip title="Open in Salesforce" arrow placement="top">
              <a
                href={opportunity.salesforceOpportunityUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="opportunity-details-crm-link"
              >
                <img
                  src={SalesForceIcon}
                  alt="sales"
                  style={{ height: "28px", width: "28px" }}
                />
              </a>
            </Tooltip>
          )}
        </Box>

        <Box className="opportunity-details-date-picker-box">
          <CustomDatePicker
            customDateRange={localDateRange}
            isSlideOverOpen={true}
            setCustomDateRange={(range) => {
              if (range?.startDate && range?.endDate) {
                setLocalDateRange({
                  startDate: range.startDate,
                  endDate: range.endDate,
                });
                setPageNumber(1);
              }
            }}
          />
        </Box>
      </Box>

      <Box className="opportunity-details-card">
        <Box className="opportunity-details-card-body">
          {/* Top Section: Details + Stakeholders */}
          <Box className="opportunity-top-section">
            <OpportunityDetailsGrid
              opportunity={opportunity}
              stakeholders={stakeholders}
            />
          </Box>

        {/* Interactions Timeline Section */}
        <Box className="opportunity-timeline-section">
          <Typography className="opportunity-section-title">
            Interactions Timeline :
          </Typography>

          <InteractionsTimeline
            items={timelineItems}
            startDate={localDateRange.startDate}
            endDate={localDateRange.endDate}
            mode="interactions"
            showLaneLabels={false}
            navigationState={{
              opportunityId: opportunity?.id,
              account: opportunity?.account,
              ...navigationContext,
            }}
          />

          <Box className="opportunity-datatable-wrapper">
            <Datatable
              columns={columns}
              rows={rows}
              rowCount={detailsData?.totalRecords ?? rows.length}
              showSpinner={isDetailsLoading}
              pageSize={pageSize}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPageNumber(1);
              }}
              onPaginationChange={(newPage) => setPageNumber(newPage + 1)}
              currentPageIndex={pageNumber - 1}
              onSortingChange={(updaterOrValue: any) => {
                setSort((prev) => {
                  const currentSorting = [
                    { id: prev.sort_field, desc: prev.sort_order === "desc" },
                  ];
                  const nextSorting =
                    typeof updaterOrValue === "function"
                      ? updaterOrValue(currentSorting)
                      : updaterOrValue;
                  if (Array.isArray(nextSorting) && nextSorting.length > 0) {
                    const nextField = nextSorting[0].id;
                    const nextOrder = nextSorting[0].desc ? "desc" : "asc";
                    // Only reset page if sort field or direction actually changed
                    if (nextField !== prev.sort_field || nextOrder !== prev.sort_order) {
                      setPageNumber(1);
                    }
                    return { sort_field: nextField, sort_order: nextOrder };
                  }
                  return prev;
                });
              }}
              minHeight="400px"
              maxHeight="600px"
              columnVisibility={tableColumnVisibility}
              timeRange=""
              calendarId={null}
              initialSort={sort}
              headerRow={40}
              bodyRow={46}
              tablePadding="0px"
              rowHoverEnabled
            />
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
  );
};

export default OpportunityDetails;
